import { Controller } from "@hotwired/stimulus"

// Steuert die synchrone KI-Profiltext-Generierung.
//
// Ablauf: Der "Generieren"-Link ist ein Turbo-POST auf generate_profile_summary.
// Der Controller dort rendert turbo_stream.update auf #profile_summary_stream_<id>
// mit dem Erfolgs-Partial (_streaming_complete). Dieses traegt den generierten Text
// im data-generated-text-Attribut und ist Stimulus-Target "result" -> resultTargetConnected()
// uebernimmt den Text automatisch in den Trix-Editor. Kein Reload noetig.
//
// Wichtig: Es wird turbo_stream.UPDATE (nicht replace) verwendet, damit der Ziel-
// Container #profile_summary_stream_<id> erhalten bleibt und auch beim 2., 3., ...
// Generieren wieder getroffen wird.
export default class extends Controller {
  static targets = ["textarea", "generateButton", "spinner", "result"]
  static values = { profileId: String }

  // Sofortiges Feedback, sobald Turbo den Request abschickt (nach evtl. Confirm-Dialog).
  showGenerating() {
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("hidden")
    if (this.hasGenerateButtonTarget) {
      this.generateButtonTarget.classList.add("opacity-60", "pointer-events-none")
      this.generateButtonTarget.setAttribute("aria-busy", "true")
    }
  }

  hideGenerating() {
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("hidden")
    if (this.hasGenerateButtonTarget) {
      this.generateButtonTarget.classList.remove("opacity-60", "pointer-events-none")
      this.generateButtonTarget.removeAttribute("aria-busy")
    }
  }

  // Feuert, sobald das Erfolgs-Partial per Turbo-Stream eingefuegt ist.
  resultTargetConnected(element) {
    this.hideGenerating()
    const text = element.dataset.generatedText
    if (!text) return
    this.loadIntoEditor(text)
  }

  // "Bearbeiten"-Button: Fokus in den Editor setzen.
  focusEditor(event) {
    if (event) event.preventDefault()
    const editor = this.findTrixEditor()
    if (editor) editor.focus()
  }

  // Legt den generierten Text in den Trix-Editor (und spiegelt ihn ins Hidden-Input
  // fuer den Formular-Submit). Wartet, falls Trix noch nicht initialisiert ist.
  loadIntoEditor(text) {
    const hiddenInput = this.findSummaryInput()
    const html = this.textToHtml(text)

    const apply = () => {
      const trix = this.findTrixEditor()
      if (trix && trix.editor) {
        trix.editor.loadHTML(html)
        return true
      }
      return false
    }

    if (!apply()) {
      const editor = this.findTrixEditor()
      if (editor) {
        editor.addEventListener("trix-initialize", apply, { once: true })
      }
      // Fallback-Polling, falls das Event bereits durch war.
      let tries = 0
      const iv = setInterval(() => {
        if (apply() || ++tries > 20) clearInterval(iv)
      }, 100)
    }

    // Hidden-Input immer mitschreiben, damit ein sofortiges Speichern den Text enthaelt.
    if (hiddenInput) {
      hiddenInput.value = html
      hiddenInput.dispatchEvent(new Event("input", { bubbles: true }))
    }
  }

  // --- Helfer ---------------------------------------------------------------

  findSummaryInput() {
    const form = this.element.closest("form")
    return form ? form.querySelector('input[type="hidden"][name*="[summary]"]') : null
  }

  findTrixEditor() {
    // trix-editor ist ueber sein input-Attribut mit dem Hidden-Input verknuepft.
    const input = this.findSummaryInput()
    if (input && input.id) {
      const linked = document.querySelector(`trix-editor[input="${input.id}"]`)
      if (linked) return linked
    }
    if (this.hasTextareaTarget && this.textareaTarget.tagName === "TRIX-EDITOR") {
      return this.textareaTarget
    }
    const form = this.element.closest("form")
    return form ? form.querySelector("trix-editor") : null
  }

  // Klartext -> einfaches HTML: Doppel-Zeilenumbrueche werden zu Absaetzen.
  textToHtml(text) {
    const cleaned = String(text).trim()
    const html = cleaned
      .replace(/\n{3,}/g, "\n\n")
      .split(/\n\n+/)
      .map((p) => p.trim().replace(/\n/g, " ").replace(/\s+/g, " "))
      .filter((p) => p.length > 0)
      .map((p) => `<p>${this.escapeHtml(p)}</p>`)
      .join("")
    return html || `<p>${this.escapeHtml(cleaned.replace(/\s+/g, " ").trim())}</p>`
  }

  escapeHtml(s) {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }
}
